import React, { Fragment } from "react";
import { Form, Stack, Button, Image, Row, Col, Spinner } from "react-bootstrap";
import { AccessControl, InputField, TextArea } from "@components";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { validationSchema, initialValues } from "./ProfileSettingData";
import { updateProfileAction } from "@redux/actions";
import { ADMIN_ROLE, INSTRUCTOR_ROLE, STUDENT_ROLE } from "@constants";
import { objectRemoveNull, uploadImage } from "@helpers";
import styles from "./styles/ProfileSetting.module.css";
import { useWindowSize } from "@hooks";
import { useState } from "react";

const ProfileSetting = () => {
  // state
  const [avatarUploading, setAvatarUploading] = useState(false);

  // variables
  let spinner = null;
  let originValues = initialValues;
  const profileLinksInputFields = [
    {
      name: "facebookLink",
      type: "text",
      placeholder: "Link to Your Facebook profile",
      prefix: (
        <span className="text-primary">
          <i className="bi bi-facebook" />
        </span>
      ),
    },
    {
      name: "linkedlnLink",
      type: "text",
      placeholder: "Link to Your LinkedIn profile",
      prefix: (
        <span className="text-info">
          <i className="bi bi-linkedin" />
        </span>
      ),
    },
    {
      name: "youtubeLink",
      type: "text",
      placeholder: "Link to Your Youtube profile",
      prefix: (
        <span className="text-danger">
          <i className="bi bi-youtube" />
        </span>
      ),
    },
  ];

  // logics before hooks
  const dispatch = useDispatch();
  const handleUpdateProfile = (values) => {
    dispatch(updateProfileAction(values));
  };

  // hooks
  const { width } = useWindowSize();
  const { profile, profileUpdating } = useSelector((state) => state.user);
  if (profile.data)
    originValues = objectRemoveNull(
      profile.data ?? initialValues,
      initialValues
    );

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
  } = useFormik({
    initialValues: originValues,
    validationSchema,
    onSubmit: handleUpdateProfile,
  });

  // logics
  const uploadAvatar = () => {
    const cropShape = "circ";
    const cropRatio = 1 / 1;
    const onUploaded = (files) => {
      if (files.length === 1) {
        setAvatarUploading(true);
        setFieldValue("avatarUrl", files[0].fileUrl);
      }
    };
    uploadImage({ cropShape, cropRatio, onUploaded });
  };

  const canSubmit = () => {
    if (JSON.stringify(originValues) === JSON.stringify(values)) return true;
    return false;
  };

  // UI partials
  spinner = (
    <div className={styles.spinner}>
      <Spinner animation="border" variant="danger" />
    </div>
  );

  const editAvatarButton =
    width >= 992 ? (
      <button
        disabled={profileUpdating.processing || avatarUploading}
        type="button"
        className={styles.editAvatarButton}
        onClick={uploadAvatar}
      >
        <Stack>
          <i className="bi bi-pencil-square h2" />
          <small>Change Avatar</small>
        </Stack>
      </button>
    ) : (
      <Button
        disabled={profileUpdating.processing || avatarUploading}
        size="sm"
        type="button"
        className="mt-2"
        onClick={uploadAvatar}
      >
        <i className="bi bi-pencil-square mr-2" />
        Change Avatar
      </Button>
    );

  const header = (
    <div>
      <h5>Your Cursus Profile</h5>
      <small className="text-muted">
        This is your public presence on Cursus. You need a profile to let people
        know who are you and what are you doing on Cursus.
      </small>
    </div>
  );

  const basicProfile = (
    <Fragment>
      <Col xs={12}>
        <h5>Basic Profile</h5>
        <small className="text-muted">Add information about yourself</small>
      </Col>
      <Col xs={8} sm={4}>
        <div className="position-relative text-center">
          <div className={styles.avatarContainer}>
            <Image
              thumbnail
              src={values.avatarUrl}
              alt="avatar"
              onLoad={() => setAvatarUploading(false)}
            />
            {avatarUploading ? spinner : null}
          </div>
          {editAvatarButton}
        </div>
      </Col>
      <Col xs={12} sm={8}>
        <Stack gap={3}>
          <Form.Group controlId="fullName">
            <InputField
              readonly={profileUpdating.processing}
              label="Full Name"
              required={true}
              name="fullName"
              placeholder="Enter your full name"
              value={values.fullName}
              onChange={handleChange}
              onBlur={handleBlur}
              touchedValue={touched.fullName}
              errorsValue={errors.fullName}
            />
          </Form.Group>
          <AccessControl targetRoles={[STUDENT_ROLE, INSTRUCTOR_ROLE]}>
            <Form.Group controlId="headLine">
              <InputField
                readonly={profileUpdating.processing}
                label="Head Line"
                name="headLine"
                postfix={
                  <span className="text-danger">
                    <strong>{30 - (values.headLine?.length ?? 0)}</strong>
                  </span>
                }
                placeholder="Add a professional headLine"
                value={values.headLine}
                onChange={handleChange}
                onBlur={handleBlur}
                touchedValue={touched.headLine}
                errorsValue={errors.headLine}
                maxLength={30}
              />
            </Form.Group>
          </AccessControl>
        </Stack>
      </Col>
      <Form.Group controlId="description">
        <TextArea
          label="Description About You"
          required={true}
          readonly={profileUpdating.processing}
          placeholder={"Write a little description about you..."}
          rows={6}
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          touchedValue={touched.description}
          errorsValue={errors.description}
        />
      </Form.Group>
    </Fragment>
  );

  const profileLinks = (
    <Fragment>
      <Col xs={12}>
        <h5>Profile Links</h5>
        <small className="text-muted">Add your external urls</small>
      </Col>
      {profileLinksInputFields.map((field, index) => (
        <Form.Group key={index} controlId="facebookLink">
          <InputField
            readonly={profileUpdating.processing}
            name={field.name}
            prefix={field.prefix}
            placeholder={field.placeholder}
            value={values[field.name]}
            onChange={handleChange}
            onBlur={handleBlur}
            touchedValue={touched[field.name]}
            errorsValue={errors[field.name]}
          />
        </Form.Group>
      ))}
    </Fragment>
  );

  // effects

  // render
  return (
    <Form noValidate onSubmit={handleSubmit}>
      <AccessControl exceptRoles={[ADMIN_ROLE]}>
        <Fragment>
          <Row>{header}</Row>
          <div className="my-3 border-bottom"></div>
        </Fragment>
      </AccessControl>
      <Row className="gy-3 align-items-end justify-content-center">
        {basicProfile}
      </Row>
      <AccessControl targetRoles={[INSTRUCTOR_ROLE]}>
        <Fragment>
          <div className="my-3 border-bottom" />
          <Row className="gy-3">{profileLinks}</Row>
        </Fragment>
      </AccessControl>
      <div className="my-3 border-bottom" />
      <Button disabled={canSubmit()} type="submit">
        Save Changes
      </Button>
    </Form>
  );
};

/* required: display name */
ProfileSetting.displayName = "ProfileSetting";

/* required: props types */
ProfileSetting.propTypes = {};

/* required: default props */
ProfileSetting.defaultProps = {};

export { ProfileSetting };
